import { Box, CircularProgress, Typography } from '@mui/material';
import ResponsiveDialogue from '../../ResponsiveDialogue/ResponsiveDialogue.tsx';
import RoundedButton from '../../RoundedButton/RoundedButton.tsx';
import type { Asset } from '../../../api/models/asset.model.ts';
import { useTheme } from '@mui/material/styles';
import { useDeleteNonLeasedAssetsMutation } from '../../../api/asset.api.ts';
import { showAlert } from '../../../redux/slices/alertSlice.ts';
import { useDispatch } from 'react-redux';
import React from 'react';
import {
	CONFIRM_NLA_DELETION,
	NLA_ASSETS_DELETED_FAIL,
	NLA_ASSETS_DELETED_SUCCESS,
} from '../../../constants/messages.tsx';

type DeleteNonLeasedAssetsModalProps = {
	open: boolean;
	onClose: () => void;
	selectedRows: Asset[];
	apiRef: React.RefObject<any>;
};

export default function DeleteNonLeasedAssetsModal({ open, onClose, selectedRows, apiRef }: DeleteNonLeasedAssetsModalProps) {

	const theme = useTheme();
	const dispatch = useDispatch();

	const [ deleteNonLeasedAssets ] = useDeleteNonLeasedAssetsMutation();

	const [ deleteInProgress, setDeleteInProgress ] = React.useState(false);

	const deleteAssets = async (selectedRows: Asset[]) => {
		const idsForDeletion = selectedRows.map((asset) => Number(asset.id));

		try {
			setDeleteInProgress(true);
			await deleteNonLeasedAssets({ ids: idsForDeletion }).unwrap();
			apiRef.current.updateRows(selectedRows.map((asset) => ({ ...asset, _action: 'delete' })));
			dispatch(showAlert({
				message: NLA_ASSETS_DELETED_SUCCESS,
				type: 'success',
			}));
			onClose();
		} catch(error) {
			dispatch(showAlert({
				message: NLA_ASSETS_DELETED_FAIL,
				type: 'error',
			}));
		} finally {
			setDeleteInProgress(false);
		}
	}

	if (!open) {
		return null;
	}

	return (
		<ResponsiveDialogue
			data-testid="delete-nla-dialogue"
			open={open}
			onClose={onClose}
			title="Delete"

		>
			<Box sx={{
				pt: 2
			}}>
				<Typography
					variant="body1"
					color={theme.palette.brand.primary}
					fontWeight={'bold'}
					paddingBottom={2}
				>
					{selectedRows.length} record{selectedRows.length !== 1 ? 's' : ''} currently selected.
				</Typography>
				<Typography variant="body1" fontWeight="bold">{CONFIRM_NLA_DELETION}</Typography>
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }}>
				<Box sx={{ pr: 2 }}>
					<RoundedButton
						variantStyle="light"
						variant="outlined"
						data-testid="cancel-nla-delete-button"
						onClick={() => {
							onClose();
						}}
					>
						Cancel
					</RoundedButton>
				</Box>
				<Box>
					<RoundedButton
						variantStyle="dark"
						variant="contained"
						data-testid="delete-nla-assets-button"
						onClick={() => {
							deleteAssets(selectedRows)
						}}
						disabled={deleteInProgress}
						width="160px"
					>
						{ deleteInProgress ? <CircularProgress size={25}/> : <>Delete Assets</> }
					</RoundedButton>
				</Box>
			</Box>
		</ResponsiveDialogue>
	);
};